import React from 'react';
import { Item, isItemAvailable, getItemPrice } from '../../types';
import StockIndicator from '../StockIndicator/StockIndicator';
import './NewOrderSelector.scss';

interface NewOrderItemSelectorProps {
  selectedCategory: { id: number; name: string; items: Item[] } | null;
  pedidoType: "Delivery" | "Caja" | "Restaurant" | "Autoservicio";
  items: Item[];
  onItemSelect: (item: Item) => void; // New callback to handle item selection
  showImage?: boolean;
  getItemStockInfo?: (itemId: number) => {
    hasShortage: boolean;
    hasWarning: boolean;
    shortageInfo?: any;
    warningInfo?: any;
  };
}

const NewOrderItemSelector: React.FC<NewOrderItemSelectorProps> = ({
  selectedCategory,
  pedidoType,
  items,
  onItemSelect,
  showImage = false,
  getItemStockInfo,
}) => {
  // Defensive check for items array
  if (!Array.isArray(items)) {
    console.error('Items is not an array:', items);
    return (
      <div className="new-order-selector-container">
        <div className="error-state">
          <p>Error: No se pudieron cargar los items</p>
        </div>
      </div>
    );
  }

  const sortedItems = [...items]
    .filter(item => item && item.item_id) // Filter out invalid items
    .filter(item => isItemAvailable(item, pedidoType)) // Filter by availability for this pedido type
    .sort((a, b) => {
      const nameA = a.item_name || '';
      const nameB = b.item_name || '';
      return nameA.localeCompare(nameB);
    });

  // When an item is clicked, delegate to parent handler
  const handleItemClick = (item: Item) => {
    onItemSelect(item);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>, item: Item) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleItemClick(item);
    }
  };

  return (
    <div className="new-order-selector-container">
      {(pedidoType !== 'Restaurant' && pedidoType !== 'Caja' && pedidoType !== 'Delivery') && (
        <h2 className="new-order-title">{selectedCategory?.name}</h2>
      )}

      {showImage ? (
        // Card Layout
        <div className="new-order-selector__items-container new-order-selector__items-container--card">
          {sortedItems.map((item) => {
            const imageUrl = item.main_image
              ? item.main_image
              : item.image_list && item.image_list.length > 0
              ? item.image_list[0]
              : '';
            const itemName = item.item_name || 'Sin nombre';
            const itemPrice = getItemPrice(item, pedidoType); // Get correct price for pedido type
            const stockInfo = getItemStockInfo ? getItemStockInfo(item.item_id) : { hasShortage: false, hasWarning: false };
            
            // Determine CSS class for stock status
            const stockClass = stockInfo.hasShortage ? 'stock-shortage' : stockInfo.hasWarning ? 'stock-warning' : '';
            
            return (
              <div
                key={item.item_id}
                className={`new-order-selector__item new-order-selector__item--card ${stockClass}`}
                onClick={() => handleItemClick(item)}
                tabIndex={0}
                onKeyPress={(e) => handleKeyPress(e, item)}
                role="button"
                aria-pressed="false"
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={itemName}
                    className="new-order-selector__item-image"
                  />
                ) : (
                  <div className="new-order-selector__item-placeholder">No Image</div>
                )}
                <div className="new-order-selector__item-info">
                  <span className="new-order-selector__item-name">
                    <strong>{itemName}</strong>
                    <StockIndicator
                      hasShortage={stockInfo.hasShortage}
                      hasWarning={stockInfo.hasWarning}
                      shortageInfo={stockInfo.shortageInfo}
                      warningInfo={stockInfo.warningInfo}
                      size="small"
                    />
                  </span>
                  <span className="new-order-selector__item-price">${itemPrice.toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // List Layout
        <div className="item-list-container">
          {sortedItems.map((item) => {
            const stockInfo = getItemStockInfo ? getItemStockInfo(item.item_id) : { hasShortage: false, hasWarning: false };
            
            // Determine CSS class for stock status
            const stockClass = stockInfo.hasShortage ? 'stock-shortage' : stockInfo.hasWarning ? 'stock-warning' : '';
            
            return (
              <div
                key={item.item_id}
                className={`item-list-item ${stockClass}`}
                onClick={() => handleItemClick(item)}
                tabIndex={0}
                onKeyPress={(e) => handleKeyPress(e, item)}
                role="button"
                aria-pressed="false"
              >
                <span className="item-name">
                  {item.item_name || 'Sin nombre'}
                  <StockIndicator
                    hasShortage={stockInfo.hasShortage}
                    hasWarning={stockInfo.hasWarning}
                    shortageInfo={stockInfo.shortageInfo}
                    warningInfo={stockInfo.warningInfo}
                    size="small"
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NewOrderItemSelector;
